import React, { useState, useEffect } from 'react';
import { apiService } from './services/api'; // Make sure this matches your import
import './style/PageStyle/EmailSettings.css'; 

const EmailSettings = () => {
  // --- State for Recipients ---
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  
  // --- State for Template Editor ---
  const [templateHtml, setTemplateHtml] = useState('');
  const [weeklyTemplateHtml, setWeeklyTemplateHtml] = useState(''); 
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // --- Tab Navigation State ---
  const [activeTab, setActiveTab] = useState('recipients'); // 'recipients' or 'template'
  const [activeTemplateTab, setActiveTemplateTab] = useState('single'); // 'single' or 'weekly'

  // --- Shared State ---
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // 1. Load Data (Emails & Template)
  useEffect(() => {
    loadEmails();
    loadTemplates();
  }, []);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const data = await apiService.getEmailSettings();
      setEmails(data);
    } catch (err) {
      console.error("Failed to load emails", err);
      setError('Failed to load email list.');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const singleData = await apiService.getTemplate();
      setTemplateHtml(singleData.template || '');
      
      try {
        const weeklyData = await apiService.getWeeklyTemplate();
        setWeeklyTemplateHtml(weeklyData.template || '');
      } catch (weeklyErr) {
        console.warn("Weekly template endpoint might not exist yet.", weeklyErr);
      }
    } catch (err) {
      console.error("Failed to load templates", err);
    }
  };

  // 2. Add Email
  const handleAdd = async (e) => {
    e.preventDefault(); 
    setSuccessMessage('');
    
    if (!newEmail.trim()) {
        setError('Please enter an email address.');
        return;
    }

    try {
      await apiService.addEmail(newEmail);
      setNewEmail('');
      setError('');
      loadEmails(); 
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add email");
    }
  };

  // 3. Delete Email
  const handleDelete = async (id) => {
    if(!window.confirm("Are you sure you want to remove this email?")) return;
    setSuccessMessage('');
    
    try {
      await apiService.deleteEmail(id);
      loadEmails(); 
    } catch (err) {
      setError("Failed to delete email");
    }
  };

  // 4. Send Test Email
  const handleSendTest = async () => {
    if (emails.length === 0) {
      setError("Please add at least one email address before testing.");
      return;
    }

    setSendingTest(true);
    setError('');
    setSuccessMessage('');

    try {
      if (activeTemplateTab === 'single') {
        await apiService.sendTestEmail(templateHtml); 
      } else {
        await apiService.sendWeeklyTestEmail(weeklyTemplateHtml);
      }
      setSuccessMessage(`Test email (${activeTemplateTab === 'single' ? 'Single Alert' : 'Weekly Report'}) sent successfully! Check your inbox.`);
    } catch (err) {
      console.error("Test failed", err);
      const msg = err.message || "Failed to send test email.";
      setError(msg);
    } finally {
      setSendingTest(false);
    }
  };

  // 5. Save Template
  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    setSuccessMessage('');
    setError('');
    try {
      if (activeTemplateTab === 'single') {
        await apiService.saveTemplate(templateHtml);
      } else {
        await apiService.saveWeeklyTemplate(weeklyTemplateHtml); 
      }
      setSuccessMessage(`${activeTemplateTab === 'single' ? 'Single Alert' : 'Weekly Report'} template saved successfully!`);
    } catch (err) {
      console.error(err);
      setError('Failed to save template.');
    } finally {
      setSavingTemplate(false);
    }
  };

  // 6. Generate Preview HTML
  const getPreviewHtml = () => {
    if (activeTemplateTab === 'single') {
      if (!templateHtml) return '<p style="color: #999; text-align: center; padding: 20px;">No template content</p>';
      return templateHtml
        .replace(/{{docName}}/g, 'Sample Contract.pdf')
        .replace(/{{Organisation}}/g, 'Acme Corp')
        .replace(/{{category}}/g, 'Legal')
        .replace(/{{format}}/g, 'PDF')
        .replace(/{{uploadDate}}/g, '15/01/2023') // <-- Changed to UK format
        .replace(/{{startDate}}/g, '01/02/2023')  // <-- Changed to UK format
        .replace(/{{expireDate}}/g, '31/12/2025') // <-- Changed to UK format
        .replace(/{{daysRemaining}}/g, '30')
        .replace(/{{status}}/g, 'Active')
        .replace(/{{uploader}}/g, 'John Doe');
    } else {
      if (!weeklyTemplateHtml) return '<p style="color: #999; text-align: center; padding: 20px;">No weekly template content</p>';
      
      // Dummy HTML list for the preview (Expiring Soon)
      const dummyExpiringListHtml = `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #eee;">Vendor Agreement.pdf</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee;">Acme Corp</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee; color: #d97706; font-weight: bold;">15/10/2023 (12 days)</td> 
        </tr>
      `; // <-- Changed date to 15/10/2023

      // Dummy HTML list for the preview (Recently Expired)
      const dummyExpiredListHtml = `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #eee;">Old Lease.docx</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee;">Real Estate LLC</td>
          <td style="padding: 10px; border-bottom: 1px solid #eee; color: #dc2626; font-weight: bold;">22/09/2023 (5 days ago)</td>
        </tr>
      `; // <-- Changed date to 22/09/2023

      return weeklyTemplateHtml
        .replace(/{{weekDate}}/g, new Date().toLocaleDateString('en-GB')) // <-- Added 'en-GB' to force UK format
        .replace(/{{totalDocs}}/g, '150')
        .replace(/{{expiringDocsCount}}/g, '1')
        .replace(/{{expiringDocsList}}/g, dummyExpiringListHtml)
        .replace(/{{recentlyExpiredCount}}/g, '1')
        .replace(/{{recentlyExpiredDocsList}}/g, dummyExpiredListHtml);
    }
  };


  return (
    <div className="email-settings-container">
      <div className="email-settings-header">
        <div className="header-text">
          <h2>Alert Email Settings</h2>
          <p>Manage recipients and customize the alert email templates.</p>
        </div>
      </div>

      <div className="settings-tabs">
        <button 
          className={`tab-btn ${activeTab === 'recipients' ? 'active' : ''}`}
          onClick={() => { setActiveTab('recipients'); setError(''); setSuccessMessage(''); }}
        >
          Recipients List
        </button>
        <button 
          className={`tab-btn ${activeTab === 'template' ? 'active' : ''}`}
          onClick={() => { setActiveTab('template'); setError(''); setSuccessMessage(''); }}
        >
          Template Editor
        </button>
      </div>

      {error && <div className="status-message error">{error}</div>}
      {successMessage && <div className="status-message success">{successMessage}</div>}

      {activeTab === 'recipients' && (
        <div className="tab-content fade-in">
          <div className="action-bar" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
            <button 
              className="test-btn" 
              onClick={handleSendTest}
              disabled={sendingTest || emails.length === 0}
              style={{ opacity: (sendingTest || emails.length === 0) ? 0.6 : 1 }}
            >
              {sendingTest ? 'Sending...' : '📨 Send Test Email'}
            </button>
          </div>

          <form className="add-email-form" onSubmit={handleAdd}>
            <input 
              type="email"
              className="email-input"
              value={newEmail} 
              onChange={(e) => setNewEmail(e.target.value)} 
              placeholder="Enter recipient email address..."
            />
            <button type="submit" className="add-btn">
              Add Email
            </button>
          </form>

          {loading && emails.length === 0 ? (
            <div className="empty-state">Loading settings...</div>
          ) : (
            <ul className="email-list">
              {emails.length > 0 ? (
                emails.map((item) => (
                  <li key={item.id} className="email-item">
                    <span className="email-text">{item.email}</span>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(item.id)}
                      title="Remove email"
                    >
                      Remove
                    </button>
                  </li>
                ))
              ) : (
                <div className="empty-state">
                  No alert emails configured yet.
                </div>
              )}
            </ul>
          )}
        </div>
      )}

      {activeTab === 'template' && (
        <div className="tab-content template-editor-wrapper fade-in">
          
          <div className="template-sub-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
            <button 
              style={{ padding: '8px 16px', cursor: 'pointer', border: 'none', background: activeTemplateTab === 'single' ? '#e0e7ff' : 'transparent', color: activeTemplateTab === 'single' ? '#4f46e5' : '#666', borderRadius: '4px', fontWeight: activeTemplateTab === 'single' ? 'bold' : 'normal' }}
              onClick={() => { setActiveTemplateTab('single'); setError(''); setSuccessMessage(''); }}
            >
              Single Email Alert
            </button>
            <button 
              style={{ padding: '8px 16px', cursor: 'pointer', border: 'none', background: activeTemplateTab === 'weekly' ? '#e0e7ff' : 'transparent', color: activeTemplateTab === 'weekly' ? '#4f46e5' : '#666', borderRadius: '4px', fontWeight: activeTemplateTab === 'weekly' ? 'bold' : 'normal' }}
              onClick={() => { setActiveTemplateTab('weekly'); setError(''); setSuccessMessage(''); }}
            >
              Weekly Report
            </button>
          </div>

          <div className="editor-info">
            {activeTemplateTab === 'single' ? (
              <>
                <p>
                  <strong>Available Placeholders (Single Alert):</strong>{' '}
                  <code>{`{{docName}}`}</code>,{' '}
                  <code>{`{{Organisation}}`}</code>,{' '}
                  <code>{`{{category}}`}</code>,{' '}
                  <code>{`{{format}}`}</code>,{' '}
                  <code>{`{{uploadDate}}`}</code>,{' '}
                  <code>{`{{startDate}}`}</code>,{' '}
                  <code>{`{{expireDate}}`}</code>,{' '}
                  <code>{`{{daysRemaining}}`}</code>,{' '}
                  <code>{`{{status}}`}</code>,{' '}
                  <code>{`{{uploader}}`}</code>
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>Available Placeholders (Weekly Report):</strong>{' '}
                  <code>{`{{weekDate}}`}</code>,{' '}
                  <code>{`{{totalDocs}}`}</code>,{' '}
                  <code>{`{{expiringDocsCount}}`}</code>,{' '}
                  <code>{`{{expiringDocsList}}`}</code>,{' '}
                  <code>{`{{recentlyExpiredCount}}`}</code>,{' '}
                  <code>{`{{recentlyExpiredDocsList}}`}</code>
                </p>
              </>
            )}
            <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
              Edit the HTML below. The preview on the right updates automatically.
            </p>
          </div>
          
          <div className="editor-split-view">
            <div className="editor-pane">
              <label>HTML Code</label>
              <textarea 
                className="html-textarea"
                value={activeTemplateTab === 'single' ? templateHtml : weeklyTemplateHtml}
                onChange={(e) => {
                  if (activeTemplateTab === 'single') {
                    setTemplateHtml(e.target.value);
                  } else {
                    setWeeklyTemplateHtml(e.target.value);
                  }
                }}
                spellCheck="false"
                placeholder="<div>Enter your HTML here...</div>"
              />
            </div>

            <div className="editor-pane">
              <label>Visual Preview</label>
              <div className="preview-box">
                <iframe 
                  title="preview"
                  srcDoc={getPreviewHtml()} 
                  style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#fff' }} 
                />
              </div>
            </div>
          </div>

          <div className="editor-actions" style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            <button 
              className="save-btn" 
              onClick={handleSaveTemplate}
              disabled={savingTemplate}
            >
              {savingTemplate ? 'Saving...' : 'Save Template'}
            </button>
            
            <button 
              className="test-btn" 
              onClick={handleSendTest}
              disabled={sendingTest || emails.length === 0}
              style={{ opacity: (sendingTest || emails.length === 0) ? 0.6 : 1, backgroundColor: '#6b7280', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}
            >
              {sendingTest ? 'Sending...' : '📨 Send Test Email'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailSettings;
